import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { AiBadge } from "@/components/AiBadge";
import { Library as LibIcon, WifiOff, Play } from "lucide-react";

// Cache the last successful library response in localStorage so paying
// viewers can still browse their rentals with no internet — the SW
// caches the API GET too but this survives cache eviction and lets us
// render instantly on first paint before the network settles.
const CACHE_KEY = "cv_library_cache:v1";

export default function Library() {
  const [films, setFilms] = useState([]);
  const [isOffline, setIsOffline] = useState(typeof navigator !== "undefined" ? !navigator.onLine : false);
  const [fromCache, setFromCache] = useState(false);

  // Hydrate immediately from localStorage so an offline reload doesn't
  // flash an empty state before the network fetch fails.
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed?.films)) {
          setFilms(parsed.films);
          setFromCache(true);
        }
      }
    } catch { /* corrupt cache — ignore */ }
  }, []);

  useEffect(() => {
    api.get("/films/library").then((r) => {
      const list = r.data || [];
      setFilms(list);
      setFromCache(false);
      try {
        window.localStorage.setItem(CACHE_KEY, JSON.stringify({ films: list, cached_at: Date.now() }));
      } catch { /* quota / private tab */ }
    }).catch(() => {
      // Network fell over — leave the localStorage-hydrated films in place
      // and flip the offline flag so the UI shows a reconnect banner.
      setIsOffline(true);
    });
  }, []);

  // Live online/offline tracking so the banner updates in-place when the
  // viewer reconnects (or drops off Wi-Fi mid-browse).
  useEffect(() => {
    const on = () => setIsOffline(false);
    const off = () => setIsOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  return (
    <div className="min-h-screen bg-ink grain">
      <Navbar />
      <div className="pt-28 px-6 sm:px-12 lg:px-20 max-w-[1600px] mx-auto pb-20">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-1">Your collection</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black">My library</h1>
        </div>

        {isOffline && (
          <div
            className="mb-6 p-4 rounded-xl bg-terracotta/10 border border-terracotta/30 flex items-start gap-3"
            data-testid="library-offline-banner"
          >
            <WifiOff className="w-5 h-5 text-terracotta shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-foreground/95">You&apos;re offline{fromCache ? " — showing your saved library" : ""}.</p>
              <p className="text-foreground/60 mt-0.5">Reconnect to play. Posters and titles stay available while you&apos;re out of range.</p>
            </div>
          </div>
        )}

        {films.length === 0 ? (
          <div className="p-10 rounded-xl bg-surface border border-white/10 text-center" data-testid="library-empty">
            <LibIcon className="w-10 h-10 mx-auto text-foreground/20 mb-3" />
            <p className="text-foreground/60">
              {isOffline ? "No cached library — reconnect to load your films." : "You haven't rented any films yet."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {films.map((f) => (
              <OfflineFilmTile key={f.id} film={f} isOffline={isOffline} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Local card variant that lets us grey out the "Play" affordance and
 * disable navigation when offline, without touching the reusable
 * ``FilmCard`` used elsewhere. Poster is loaded via the standard API
 * URL which the SW caches on the network-first pass, so the image
 * survives an offline reload.
 */
function OfflineFilmTile({ film, isOffline }) {
  const poster = `${API}/films/${film.id}/poster${film.poster_updated_at ? `?v=${encodeURIComponent(film.poster_updated_at)}` : ""}`;
  const disabled = isOffline;
  const Wrapper = disabled ? "div" : Link;
  const wrapperProps = disabled ? { role: "button", "aria-disabled": true } : { to: `/film/${film.id}` };

  return (
    <div data-testid={`library-film-${film.id}`}>
      <Wrapper
        {...wrapperProps}
        className={`block relative aspect-[2/3] rounded-lg overflow-hidden bg-surface border border-white/10 group ${
          disabled ? "cursor-not-allowed opacity-70" : "hover:border-terracotta/30 transition-colors"
        }`}
      >
        <img
          src={poster}
          alt={film.title}
          loading="lazy"
          className="w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.opacity = 0.2; }}
        />
        <AiBadge film={film} className="absolute top-2 left-2" />
        {!disabled ? (
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-4">
            <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-terracotta/95 text-white text-xs font-semibold">
              <Play className="w-3 h-3" /> Play
            </span>
          </div>
        ) : (
          <div className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/70 backdrop-blur border border-white/10 text-[10px] font-semibold uppercase tracking-widest text-terracotta">
            <WifiOff className="w-3 h-3" /> Offline
          </div>
        )}
      </Wrapper>
      <div className="mt-2">
        <p className="font-semibold text-sm line-clamp-1">{film.title}</p>
        {film.purchased_at && (
          <p className="text-[11px] text-foreground/50 mt-0.5">Owned since {new Date(film.purchased_at).toLocaleDateString()}</p>
        )}
        {disabled && (
          <p className="text-[11px] text-terracotta mt-0.5" data-testid={`library-reconnect-hint-${film.id}`}>
            Reconnect to play
          </p>
        )}
      </div>
    </div>
  );
}
